/**
 * +field: The Hollow Barrow walkable geometry — a sunken shrine under Sunmeadow (the starter
 * dungeon). Same authoring approach as venomAltarLayout.ts: axis-aligned rooms, straight corridor
 * strips, stair flights from riseBetween, doors from doorAcross. Original layout.
 *
 *   barrow_mouth (top of the stairs, entrance)
 *        │ stairs down
 *   ossuary ── lantern_crypt
 *                   │
 *   bone_gallery ── bonecaller_shrine
 *        │ stairs down, sealed
 *   kings_tomb
 *
 * Coordinates are yards (x east, y north = away from the default camera).
 */
import type { CorridorDef, DoorDef, Polygon, PropHint, Rect, RoomDef, Vec2 } from '@keystone/sim';
import { doorAcross, riseBetween } from '../dungeons/elevation';
import { mapScale } from '../dungeons/geometry';
import { BARROW_ENCOUNTER } from './ids';

const M = mapScale(1);

/** Floor levels (visual only): the mouth near the meadow surface, the shrine halls, the tomb deepest. */
export const BARROW_LEVEL = { tomb: 0, halls: 3, mouth: 7 } as const;

const ROOM_BOXES = {
  barrow_mouth: { name: 'Barrow Mouth', box: [0, 0, 24, 18], elevation: BARROW_LEVEL.mouth },
  ossuary: { name: 'Ossuary', box: [-6, 34, 30, 66], elevation: BARROW_LEVEL.halls },
  lantern_crypt: { name: 'Lantern Crypt', box: [46, 44, 80, 74], elevation: BARROW_LEVEL.halls },
  bonecaller_shrine: { name: "Bonecaller's Shrine", box: [48, 90, 80, 118], elevation: BARROW_LEVEL.halls },
  bone_gallery: { name: 'Gallery of Bones', box: [12, 92, 40, 114], elevation: BARROW_LEVEL.halls },
  kings_tomb: { name: "The Hollow King's Tomb", box: [6, 132, 48, 168], elevation: BARROW_LEVEL.tomb },
} as const satisfies Record<string, { name: string; box: readonly [number, number, number, number]; elevation: number }>;

export type BarrowRoomId = keyof typeof ROOM_BOXES;

export const BARROW_ROOMS: RoomDef[] = (Object.keys(ROOM_BOXES) as BarrowRoomId[]).map((id) => {
  const { name, box, elevation } = ROOM_BOXES[id];
  return { id, name, shape: M.rectPoly(box[0], box[1], box[2], box[3]), elevation };
});

export const barrowRoomRect = (id: BarrowRoomId): Rect => {
  const [x0, y0, x1, y1] = ROOM_BOXES[id].box;
  return M.rect(x0, y0, x1, y1);
};

interface CorridorSpec {
  id: string;
  from: BarrowRoomId;
  to: BarrowRoomId;
  a: [number, number];
  b: [number, number];
  width: number;
  stairsLastYards?: number;
  landing?: number;
}

const CORRIDOR_SPECS: CorridorSpec[] = [
  // The long stair down from the meadow into the barrow (7 → 3 yd).
  { id: 'c_mouth_ossuary', from: 'barrow_mouth', to: 'ossuary', a: [12, 14], b: [12, 38], width: 7 },
  { id: 'c_ossuary_crypt', from: 'ossuary', to: 'lantern_crypt', a: [26, 58], b: [50, 58], width: 7 },
  { id: 'c_crypt_shrine', from: 'lantern_crypt', to: 'bonecaller_shrine', a: [63, 70], b: [63, 94], width: 7 },
  { id: 'c_shrine_gallery', from: 'bonecaller_shrine', to: 'bone_gallery', a: [52, 103], b: [36, 103], width: 7 },
  // Down into the tomb, with a landing for the seal.
  { id: 'c_gallery_tomb', from: 'bone_gallery', to: 'kings_tomb', a: [26, 110], b: [26, 136], width: 8, stairsLastYards: 10, landing: 4 },
];

const specById = new Map(CORRIDOR_SPECS.map((c) => [c.id, c]));
const centreline = (c: CorridorSpec): [Vec2, Vec2] => [M.pt(c.a[0], c.a[1]), M.pt(c.b[0], c.b[1])];

export const BARROW_CORRIDORS: CorridorDef[] = CORRIDOR_SPECS.map((c) => {
  const [a, b] = centreline(c);
  const def: CorridorDef = { id: c.id, from: c.from, to: c.to, shape: M.strip(c.a, c.b, c.width) };
  const rise = riseBetween(a, b, barrowRoomRect(c.from), barrowRoomRect(c.to), ROOM_BOXES[c.from].elevation, ROOM_BOXES[c.to].elevation, {
    stripWidth: c.width,
    ...(c.stairsLastYards !== undefined ? { lastYards: c.stairsLastYards } : {}),
    ...(c.landing !== undefined ? { landing: c.landing } : {}),
  });
  if (rise) def.rises = [rise];
  return def;
});

type DoorSpec = { id: string; corridor: string; at: 'from' | 'to'; style: DoorDef['style']; seals?: string };

/** An (open) iron gate at the foot of the entry stair, and the King's stone seal. */
const DOOR_SPECS: DoorSpec[] = [
  { id: 'd_ossuary_gate', corridor: 'c_mouth_ossuary', at: 'to', style: 'portcullis' },
  { id: 'd_king_seal', corridor: 'c_gallery_tomb', at: 'to', style: 'stone', seals: BARROW_ENCOUNTER.king },
];

export const BARROW_DOORS: DoorDef[] = DOOR_SPECS.map((d) => {
  const c = specById.get(d.corridor)!;
  const [a, b] = centreline(c);
  return doorAcross({
    id: d.id,
    corridorId: c.id,
    a,
    b,
    fromRoom: c.from,
    toRoom: c.to,
    fromRect: barrowRoomRect(c.from),
    toRect: barrowRoomRect(c.to),
    stripWidth: c.width,
    at: d.at,
    style: d.style,
    ...(d.seals ? { sealsEncounter: d.seals } : {}),
  });
});

// ---- obstacles: ossuary bone pillars, the crypt's lantern plinths, tomb pillars ----------------

const square = (c: Vec2, h: number): Polygon => ({
  points: [
    { x: c.x - h, y: c.y - h },
    { x: c.x + h, y: c.y - h },
    { x: c.x + h, y: c.y + h },
    { x: c.x - h, y: c.y + h },
  ],
});

/** Pillars (yards). Kept off the corridor axes and the pack spots. */
export const BARROW_PILLARS: Vec2[] = [
  // Ossuary: two bone-stacked pillars either side of the hall's middle.
  { x: 2, y: 56 },
  { x: 22, y: 42 },
  // Lantern Crypt: four plinths around the central lantern.
  { x: 54, y: 66 },
  { x: 72, y: 52 },
  // King's tomb: four pillars framing the arena.
  { x: 14, y: 140 },
  { x: 40, y: 140 },
  { x: 14, y: 160 },
  { x: 40, y: 160 },
];

export const BARROW_OBSTACLES: Polygon[] = BARROW_PILLARS.map((p) => square(p, 1.2));

// ---- authored hero props ------------------------------------------------------------------------

type Side = 'n' | 's' | 'e' | 'w';
function onWall(room: BarrowRoomId, side: Side, t: number, out = 1.6): Vec2 {
  const [x0, y0, x1, y1] = ROOM_BOXES[room].box;
  const r1 = (v: number): number => Math.round(v * 10) / 10;
  if (side === 'n') return { x: r1(x0 + (x1 - x0) * t), y: y1 + out };
  if (side === 's') return { x: r1(x0 + (x1 - x0) * t), y: y0 - out };
  if (side === 'e') return { x: x1 + out, y: r1(y0 + (y1 - y0) * t) };
  return { x: x0 - out, y: r1(y0 + (y1 - y0) * t) };
}
const FACE: Record<Side, number> = { n: -Math.PI / 2, s: Math.PI / 2, e: Math.PI, w: 0 };
const hint = (kind: string, room: BarrowRoomId, side: Side, t: number, extra: Partial<PropHint> = {}, out?: number): PropHint => ({
  kind,
  pos: onWall(room, side, t, out),
  facing: FACE[side],
  roomId: room,
  ...extra,
});
const axis = (corridorId: string): number => {
  const [a, b] = centreline(specById.get(corridorId)!);
  return Math.round(Math.atan2(b.y - a.y, b.x - a.x) * 1e4) / 1e4;
};

/**
 * Prop kinds (renderer kit for the 'hollow_barrow' theme): barrow_arch, crypt_arch, sarcophagus,
 * bone_pile, skull_mound, candles, urn, lantern_plinth (inside its obstacle), grave_banner,
 * root_tangle, bone_pillar / tomb_pillar (inside obstacles), barrow_throne, altar_dais, brazier,
 * cobweb, floor_rune (flat decal). Ledge props sit outside the walkable floor; nothing blocks.
 */
export const BARROW_PROPS: PropHint[] = [
  // Barrow Mouth: the turf-and-stone arch you came through, roots through the ceiling, urns.
  hint('barrow_arch', 'barrow_mouth', 's', 0.5, { scale: 1.4 }),
  hint('root_tangle', 'barrow_mouth', 'w', 0.5),
  hint('root_tangle', 'barrow_mouth', 'e', 0.4),
  hint('urn', 'barrow_mouth', 'n', 0.15),
  hint('urn', 'barrow_mouth', 'n', 0.85),
  hint('candles', 'barrow_mouth', 'w', 0.85),
  // Ossuary: bone-stacked pillars, skull mounds and sarcophagi along the walls.
  ...BARROW_PILLARS.slice(0, 2).map((pos): PropHint => ({ kind: 'bone_pillar', pos, roomId: 'ossuary' })),
  hint('crypt_arch', 'ossuary', 's', (12 + 6) / 36),
  hint('skull_mound', 'ossuary', 'w', 0.3),
  hint('bone_pile', 'ossuary', 'w', 0.75),
  hint('sarcophagus', 'ossuary', 'n', 0.2),
  hint('sarcophagus', 'ossuary', 'n', 0.5),
  hint('sarcophagus', 'ossuary', 'n', 0.8),
  hint('cobweb', 'ossuary', 'e', 0.15),
  hint('candles', 'ossuary', 'e', 0.85),
  // Lantern Crypt: plinths with ghost-lanterns, banners.
  ...BARROW_PILLARS.slice(2, 4).map((pos): PropHint => ({ kind: 'lantern_plinth', pos, roomId: 'lantern_crypt' })),
  { kind: 'crypt_arch', pos: { x: 45, y: 58 }, facing: axis('c_ossuary_crypt'), corridorId: 'c_ossuary_crypt' },
  hint('grave_banner', 'lantern_crypt', 'n', 0.25),
  hint('grave_banner', 'lantern_crypt', 'n', 0.75),
  hint('urn', 'lantern_crypt', 'e', 0.3),
  hint('urn', 'lantern_crypt', 'e', 0.7),
  hint('bone_pile', 'lantern_crypt', 's', 0.8),
  // Bonecaller's Shrine: an altar dais on the east wall, rune circle on the floor, braziers.
  hint('altar_dais', 'bonecaller_shrine', 'e', 0.5, { scale: 1.3 }),
  { kind: 'floor_rune', pos: { x: 66, y: 104 }, scale: 9, roomId: 'bonecaller_shrine' },
  hint('brazier', 'bonecaller_shrine', 'n', 0.2),
  hint('brazier', 'bonecaller_shrine', 'n', 0.8),
  hint('brazier', 'bonecaller_shrine', 's', 0.8),
  hint('skull_mound', 'bonecaller_shrine', 'e', 0.15),
  // Gallery of Bones: bone piles and sarcophagi lining the walk to the tomb stair.
  hint('bone_pile', 'bone_gallery', 'w', 0.3),
  hint('bone_pile', 'bone_gallery', 'w', 0.7),
  hint('sarcophagus', 'bone_gallery', 's', 0.25),
  hint('sarcophagus', 'bone_gallery', 's', 0.75),
  hint('candles', 'bone_gallery', 'n', 0.85),
  hint('cobweb', 'bone_gallery', 'n', 0.15),
  // The King's tomb: throne on the north wall, pillars, braziers in the corners.
  ...BARROW_PILLARS.slice(4).map((pos): PropHint => ({ kind: 'tomb_pillar', pos, roomId: 'kings_tomb' })),
  hint('barrow_throne', 'kings_tomb', 'n', 0.5, { scale: 1.6 }, 2),
  hint('grave_banner', 'kings_tomb', 'n', 0.25),
  hint('grave_banner', 'kings_tomb', 'n', 0.75),
  hint('brazier', 'kings_tomb', 'w', 0.1),
  hint('brazier', 'kings_tomb', 'e', 0.1),
  hint('brazier', 'kings_tomb', 'w', 0.9),
  hint('brazier', 'kings_tomb', 'e', 0.9),
  hint('sarcophagus', 'kings_tomb', 'w', 0.5),
  hint('sarcophagus', 'kings_tomb', 'e', 0.5),
];

/** Boss arenas (yards). */
export const BARROW_ARENA = {
  bonecaller: barrowRoomRect('bonecaller_shrine'),
  king: barrowRoomRect('kings_tomb'),
} as const;

export const BARROW_BOUNDS: Rect = M.rect(-12, -8, 86, 174);

/** Run start: at the top of the entry stair, just inside the barrow arch. */
export const BARROW_ENTRANCE: Vec2 = M.pt(12, 5);
